import { Door, Location, VisitorGroup } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { useState } from 'react';
import useModalContext from 'v2/components/Modal/useModalContext';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import WizardModalTemplate, { WizardStep } from 'v2/components/WizardModal/WizardModalTemplate';
import DeviceGroupDevicesStep from './DeviceGroupDevicesStep';
import DeviceGroupNameStep from './DeviceGroupNameStep';
import DeviceGroupUserGroupsStep from './DeviceGroupUserGroupsStep';

interface WizardData {
  groupId: string;
  groupName: string;
}

interface Props {
  deviceGroups: Location[];
  allDoors: Door[];
  allVisitorGroups: VisitorGroup[];
  createDeviceGroup: (name: string) => Promise<{ success: boolean; groupId?: string; errorMessage?: string }>;
  assignUserGroupsToDeviceGroup: (
    groupId: string,
    visitorGroupIds: string[]
  ) => Promise<{ success: boolean; errorMessage?: string }>;
  updateDeviceGroupWithDoors: (
    groupId: string,
    groupName: string,
    doorIds: string[]
  ) => Promise<{ success: boolean; errorMessage?: string }>;
}

const AddDeviceGroupWizard: React.FC<Props> = ({
  deviceGroups,
  allDoors,
  allVisitorGroups,
  createDeviceGroup,
  assignUserGroupsToDeviceGroup,
  updateDeviceGroupWithDoors
}) => {
  const { translation } = useTranslation();
  const { closeModal } = useModalContext();
  const { showSnackbar } = useSnackbarContext();
  const [wizardData, setWizardData] = useState<WizardData | null>(null);

  const existingNames = deviceGroups.map((group) => group.name);

  const steps: WizardStep[] = [
    { label: translation.group_name },
    { label: translation.user_groups, description: translation.optional },
    { label: translation.devices, description: translation.optional }
  ];

  const handleGroupCreated = (groupId: string, groupName: string) => {
    setWizardData({ groupId, groupName });
  };

  const handleFinish = () => {
    showSnackbar(translation.saved_changes, { severity: 'success' });
    closeModal();
  };

  return (
    <WizardModalTemplate title={translation.add_new_device_group} steps={steps} onFinish={handleFinish}>
      <DeviceGroupNameStep
        existingNames={existingNames}
        onCreate={handleGroupCreated}
        createDeviceGroup={createDeviceGroup}
      />
      <DeviceGroupUserGroupsStep
        groupId={wizardData?.groupId ?? null}
        allVisitorGroups={allVisitorGroups}
        assignUserGroupsToDeviceGroup={assignUserGroupsToDeviceGroup}
      />
      <DeviceGroupDevicesStep
        groupId={wizardData?.groupId ?? null}
        groupName={wizardData?.groupName ?? null}
        allDoors={allDoors}
        updateDeviceGroupWithDoors={updateDeviceGroupWithDoors}
      />
    </WizardModalTemplate>
  );
};

export default AddDeviceGroupWizard;
